import {
  Box,
  Button,
  Fieldset,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Upload } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'

import { Field, type TranslateFn } from '@mangovoice/mango-ui'

import type { FeatureCode, SoundClipApi } from '../api/soundClipApi.types'
import { FEATURE_CODE_OPTIONS } from '../api/soundClipApi.types'
import {
  useCreateSoundClip,
  useGetTakenFeatureCodes,
} from '../queries/soundClipQueries'
import { type SoundClipTranslateFn, text } from '../types/i18n.types'
import { validateAudioFile } from '../utils/audioFile'

export interface SoundClipCreatePageProps {
  api: SoundClipApi
  pbxId: number
  onBack: () => void
  onSuccess?: () => void
  t?: TranslateFn | SoundClipTranslateFn
}

export const SoundClipCreatePage: React.FC<SoundClipCreatePageProps> = ({
  api,
  pbxId,
  onBack,
  onSuccess,
  t,
}) => {
  const createMutation = useCreateSoundClip(api, pbxId)
  const { data: takenCodes = [] } = useGetTakenFeatureCodes(api, pbxId)

  const [name, setName] = useState('')
  const [featureCode, setFeatureCode] = useState<FeatureCode>(0)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<{ name?: string; audioFile?: string }>(
    {},
  )

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropzoneRef = useRef<HTMLDivElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const validate = (): boolean => {
    const next: typeof errors = {}
    if (!name.trim()) {
      next.name = text(t, 'soundClips.create.nameRequired', 'Name is required.')
    }
    if (!audioFile) {
      next.audioFile = text(
        t,
        'soundClips.create.audioFileRequired',
        'An audio file is required.',
      )
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!validate()) return

      try {
        await createMutation.mutateAsync({
          name: name.trim(),
          feature_code: featureCode,
          pbx_id: pbxId,
          audio_file: audioFile!,
        })
        onSuccess?.()
        onBack()
      } catch {
        // Error surfaced via createMutation.error in the form below.
      }
    },
    [name, featureCode, audioFile, pbxId, createMutation, onBack, onSuccess],
  )

  const acceptFile = useCallback(
    (file: File) => {
      const error = validateAudioFile(file, t)
      if (error) {
        // Clear any previously-accepted file so the dropzone doesn't show a
        // valid file's name alongside the rejection error.
        setAudioFile(null)
        setErrors((prev) => ({ ...prev, audioFile: error }))
        return
      }
      setAudioFile(file)
      setErrors((prev) => ({ ...prev, audioFile: undefined }))
    },
    [t],
  )

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) acceptFile(file)
    },
    [acceptFile],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) acceptFile(file)
      // Reset so re-selecting the same file (after a rejection or Remove) still
      // fires onChange — the value is otherwise unchanged and the event won't fire.
      e.target.value = ''
    },
    [acceptFile],
  )

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <Flex
        direction={'row'}
        justifyContent={'flex-start'}
        alignItems={'center'}
        gap={4}
        mb={4}
      >
        <Text
          fontSize="sm"
          color="fg.muted"
          cursor="pointer"
          _hover={{ color: 'fg', textDecoration: 'underline' }}
          onClick={onBack}
        >
          {text(t, 'soundClips.edit.backToList', '← Back to list')}
        </Text>

        <Heading size="lg" mb={0}>
          {text(t, 'soundClips.create.title', 'New Sound Clip')}
        </Heading>
      </Flex>

      <Box as="form" onSubmit={handleSubmit} maxW="560px">
        <VStack gap={5} align="stretch">
          <Field.Root required invalid={!!errors.name}>
            <Field.Label>
              {text(t, 'soundClips.create.nameLabel', 'Name')}
            </Field.Label>
            <Input
              placeholder={text(
                t,
                'soundClips.create.namePlaceholder',
                'Enter sound clip name',
              )}
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (errors.name)
                  setErrors((prev) => ({ ...prev, name: undefined }))
              }}
            />
            {errors.name && <Field.ErrorText>{errors.name}</Field.ErrorText>}
          </Field.Root>

          <Fieldset.Root>
            <Fieldset.Legend>
              {text(t, 'soundClips.create.featureCodeLabel', 'Feature Code')}
            </Fieldset.Legend>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={featureCode}
                onChange={(e) =>
                  setFeatureCode(Number(e.target.value) as FeatureCode)
                }
              >
                {FEATURE_CODE_OPTIONS.map((opt) => {
                  const taken = takenCodes.find((tc) => tc.code === opt.value)
                  return (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={!!taken}
                    >
                      {taken
                        ? `${opt.label} (taken by ${taken.name})`
                        : opt.label}
                    </option>
                  )
                })}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Fieldset.Root>

          <Field.Root required invalid={!!errors.audioFile}>
            <Field.Label>
              {text(t, 'soundClips.create.audioFileLabel', 'Audio File')}
            </Field.Label>
            <Box
              ref={dropzoneRef}
              border="2px dashed"
              borderColor={
                isDragOver
                  ? 'blue.400'
                  : errors.audioFile
                    ? 'red.400'
                    : 'border.default'
              }
              borderRadius="lg"
              p={6}
              textAlign="center"
              cursor="pointer"
              bg={isDragOver ? 'blue.50' : 'bg.subtle'}
              transition="all 0.15s"
              _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e: React.DragEvent) => {
                e.preventDefault()
                setIsDragOver(true)
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleFileDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/wav,audio/x-wav,audio/mpeg,.wav,.mp3"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {audioFile ? (
                <VStack gap={1}>
                  <Icon as={Upload} boxSize={6} color="brand.fg" />
                  <Text fontWeight="medium">{audioFile.name}</Text>
                  <Text fontSize="sm" color="fg.muted">
                    {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    borderRadius="lg"
                    colorPalette="error"
                    onClick={(e) => {
                      e.stopPropagation()
                      setAudioFile(null)
                    }}
                  >
                    Remove
                  </Button>
                </VStack>
              ) : (
                <VStack gap={1}>
                  <Icon as={Upload} boxSize={8} color="fg.muted" />
                  <Text>
                    {text(
                      t,
                      'soundClips.create.audioFilePlaceholder',
                      'Drag & drop a WAV or MP3 file here, or click to browse',
                    )}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {text(
                      t,
                      'soundClips.create.audioFileAccepted',
                      'WAV, MP3 (max 25 MB)',
                    )}
                  </Text>
                </VStack>
              )}
            </Box>
            {errors.audioFile && (
              <Field.ErrorText>{errors.audioFile}</Field.ErrorText>
            )}
          </Field.Root>

          {createMutation.isError && (
            <Box
              role="alert"
              colorPalette="error"
              borderWidth="1px"
              borderColor="border.error"
              bg="colorPalette.subtle"
              borderRadius="md"
              p={3}
            >
              <Text fontSize="sm" color="fg.error">
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : text(
                      t,
                      'soundClips.create.uploadFailed',
                      'Upload failed. Please try again.',
                    )}
              </Text>
            </Box>
          )}

          <HStack gap={3} justify="flex-end" pt={2}>
            <Button variant="outline" borderRadius="lg" onClick={onBack}>
              {text(t, 'soundClips.create.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              bg="brand.solid"
              color="brand.contrast"
              borderRadius="lg"
              _hover={{ bg: 'brand.emphasized' }}
              _active={{ bg: 'brand.emphasized' }}
              loading={createMutation.isPending}
              loadingText={text(
                t,
                'soundClips.create.creating',
                'Uploading...',
              )}
            >
              {text(t, 'soundClips.create.submit', 'Upload Sound Clip')}
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  )
}
